/**
 * Approved overage hours card on ProjectDetailPage. Backend service shipped in
 * Phase 37 Plan 37-06. Visible only when paymentTypeId === SLA Hour Package
 * AND overLimitAllowed; editable by admin or project PM via `canManage`.
 *
 * Quick 261103-rwm — retroactive approval: ANY month can be approved after the
 * fact (the backend has no date restriction). The card lists one row per month
 * (current month, every month with overage, recent approval history, and any
 * month picked by the user). A row with an existing approval row is changed via
 * PATCH, a row without one is created via the POST upsert.
 */
import { useMemo, useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../common/Button';
import { MonthYearPicker } from '../ui/MonthYearPicker';
import {
  useProjectMonthlyOverageApprovals,
  useUpsertProjectMonthlyOverageApproval,
  useUpdateProjectMonthlyOverageApproval,
} from '../../hooks/useProjectMonthlyOverageApprovals';
import { DATE_PLACEHOLDER, formatMonthYear } from '../../utils/dateFormat';
import { useProjectUninvoicedHours } from '../../hooks/useProjectUninvoicedHours';
import type { MonthlyOverageApproval } from '../../services/api/projectMonthlyOverageApprovals';

export interface OverageApprovalCardProps {
  projectId: number;
  /** From `Project.paymentTypeId`; null when the project is on the default template. */
  paymentTypeId: number | null;
  /** From `Project.overLimitAllowed`; backend default is false. */
  overLimitAllowed: boolean;
  /** Constant from `frontend/src/constants/typeIds.ts`. */
  slaBalicekPaymentTypeId: number;
  /** Admin OR project PM — write access for the upsert button. */
  canManage: boolean;
}

type ApprovalStatus = 'missing' | 'less' | 'ok';

interface MonthRow {
  period: string;
  approval: MonthlyOverageApproval | undefined;
  /** Computed overage for the month; null when the month has no outstanding overage. */
  overageHours: number | null;
  status: ApprovalStatus | null;
  userAdded: boolean;
}

/** Approved history rows without outstanding overage that stay visible. */
const HISTORY_ROW_LIMIT = 6;
const HOURS_PATTERN = /^[0-9]+(\.[0-9]{1,2})?$/;

function currentYearMonth(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function formatYearMonth(yyyymm: string): string {
  // MKS-351 — "October 2026" from the shared util; falls back to the raw
  // period string when it is not a well-formed YYYY-MM.
  const label = formatMonthYear(yyyymm);
  return label === DATE_PLACEHOLDER ? yyyymm : label;
}

function approvedLabel(approval: MonthlyOverageApproval | undefined): string {
  if (!approval) return '— h';
  // approvedHours can be null: PM acknowledged the overage without committing a count.
  return approval.approvedHours !== null ? `${approval.approvedHours} h` : 'Acknowledged';
}

function StatusChip({ row }: { row: MonthRow }) {
  if (row.status === null) return null;
  const base = 'rounded-full px-2 py-0.5 text-[11px] font-medium';
  if (row.status === 'ok') {
    return <span className={`${base} bg-green-100 text-green-800`}>Covers overage</span>;
  }
  if (row.status === 'less') {
    return (
      <span className={`${base} bg-amber-100 text-amber-800`}>
        Approved {row.approval?.approvedHours ?? '0.00'} h &lt; overage
      </span>
    );
  }
  return <span className={`${base} bg-amber-100 text-amber-800`}>Needs approval</span>;
}

interface MonthRowViewProps {
  row: MonthRow;
  isLoading: boolean;
  canManage: boolean;
  isEditing: boolean;
  draftHours: string;
  error: string | undefined;
  isPending: boolean;
  onDraftChange: (value: string) => void;
  onApproveAll: () => void;
  onAcknowledge: () => void;
  onOpenEditor: () => void;
  onSave: () => void;
  onCancel: () => void;
}

function MonthRowView({
  row,
  isLoading,
  canManage,
  isEditing,
  draftHours,
  error,
  isPending,
  onDraftChange,
  onApproveAll,
  onAcknowledge,
  onOpenEditor,
  onSave,
  onCancel,
}: MonthRowViewProps) {
  const label = formatYearMonth(row.period);
  const hasNumericApproval = row.approval !== undefined && row.approval.approvedHours !== null;
  const showApproveAll = row.overageHours !== null && row.status !== 'ok';
  const inputId = `overage-approval-input-${row.period}`;

  return (
    <div data-testid={`overage-row-${row.period}`} className="py-2 first:pt-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-ms-text-muted">{label}</div>
          {!isEditing && (
            <div className="text-[20px] font-bold tabular-nums text-gray-900">
              {isLoading ? '…' : approvedLabel(row.approval)}
            </div>
          )}
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            {row.overageHours !== null && (
              <span className="text-[12px] tabular-nums text-ms-text-secondary">
                Overage {row.overageHours.toFixed(2)} h
              </span>
            )}
            <StatusChip row={row} />
          </div>
        </div>
        {!isEditing && canManage && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {showApproveAll && (
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={onApproveAll}
                isLoading={isPending}
              >
                {`Approve all ${row.overageHours!.toFixed(2)} h`}
              </Button>
            )}
            {row.approval === undefined && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={onAcknowledge}
                disabled={isPending}
              >
                Acknowledge only
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={onOpenEditor}
              disabled={isPending}
            >
              {hasNumericApproval ? 'Update' : 'Approve hours'}
            </Button>
          </div>
        )}
      </div>

      {isEditing && (
        <div className="mt-2 space-y-2 border-t border-gray-100 pt-2">
          <label htmlFor={inputId} className="block text-[12px] font-medium text-gray-700">
            Approved hours for {label}
          </label>
          <div className="flex items-center gap-2">
            <input
              id={inputId}
              type="text"
              inputMode="decimal"
              value={draftHours}
              onChange={(e) => onDraftChange(e.target.value)}
              className="w-32 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g. 12.50"
              pattern="^[0-9]+(\.[0-9]{1,2})?$"
            />
            <span className="text-[12px] text-ms-text-secondary">h</span>
            <Button variant="primary" size="sm" type="button" onClick={onSave} isLoading={isPending}>
              Save
            </Button>
            <Button variant="ghost" size="sm" type="button" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {error && <p className="mt-1 text-[12px] text-red-600">{error}</p>}
    </div>
  );
}

export function OverageApprovalCard({
  projectId,
  paymentTypeId,
  overLimitAllowed,
  slaBalicekPaymentTypeId,
  canManage,
}: OverageApprovalCardProps) {
  const isBalicek = paymentTypeId === slaBalicekPaymentTypeId;
  const enabled = isBalicek && overLimitAllowed;

  const { data: approvals, isLoading } = useProjectMonthlyOverageApprovals(
    enabled ? projectId : undefined,
  );
  // Same cache key as UninvoicedHoursCard — no extra request when both are mounted.
  const { data: uninvoiced } = useProjectUninvoicedHours(projectId, enabled);
  const upsertMutation = useUpsertProjectMonthlyOverageApproval(projectId);
  const updateMutation = useUpdateProjectMonthlyOverageApproval(projectId);

  const [editingPeriod, setEditingPeriod] = useState<string | null>(null);
  const [draftHours, setDraftHours] = useState('');
  const [pendingPeriod, setPendingPeriod] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [addedPeriods, setAddedPeriods] = useState<string[]>([]);
  const [pickerValue, setPickerValue] = useState('');
  const [pickerError, setPickerError] = useState<string | null>(null);

  const period = currentYearMonth();

  const overageByPeriod = useMemo(() => {
    const map = new Map<string, { hours: number; status: ApprovalStatus }>();
    const summary = uninvoiced?.data;
    if (summary && summary.kind === 'overage') {
      for (const bucket of summary.byMonth) {
        if (bucket.approval) {
          map.set(bucket.period, { hours: bucket.hours, status: bucket.approval.status });
        }
      }
    }
    return map;
  }, [uninvoiced]);

  const approvalByPeriod = useMemo(() => {
    const map = new Map<string, MonthlyOverageApproval>();
    for (const a of approvals ?? []) map.set(a.periodYearMonth, a);
    return map;
  }, [approvals]);

  const rows = useMemo<MonthRow[]>(() => {
    const periods = new Set<string>([period, ...addedPeriods]);
    for (const p of overageByPeriod.keys()) periods.add(p);
    for (const p of approvalByPeriod.keys()) periods.add(p);

    const all = [...periods]
      .sort((a, b) => b.localeCompare(a))
      .map<MonthRow>((p) => {
        const overage = overageByPeriod.get(p);
        return {
          period: p,
          approval: approvalByPeriod.get(p),
          overageHours: overage ? overage.hours : null,
          status: overage ? overage.status : null,
          userAdded: addedPeriods.includes(p),
        };
      });

    const alwaysVisible = (r: MonthRow) =>
      r.overageHours !== null || r.period === period || r.userAdded;
    const history = new Set(
      all
        .filter((r) => !alwaysVisible(r))
        .slice(0, HISTORY_ROW_LIMIT)
        .map((r) => r.period),
    );
    return all.filter((r) => alwaysVisible(r) || history.has(r.period));
  }, [period, addedPeriods, overageByPeriod, approvalByPeriod]);

  if (!enabled) return null;

  const prefillFor = (p: string): string => {
    const stored = approvalByPeriod.get(p)?.approvedHours;
    if (stored !== null && stored !== undefined) return stored;
    const overage = overageByPeriod.get(p);
    return overage ? overage.hours.toFixed(2) : '';
  };

  const clearError = (p: string) =>
    setErrors((prev) => {
      if (!(p in prev)) return prev;
      const next = { ...prev };
      delete next[p];
      return next;
    });

  /** Single save path: PATCH when the month already has an approval row, POST upsert otherwise. */
  const savePeriod = async (p: string, hours: string | null, failure: string): Promise<boolean> => {
    clearError(p);
    setPendingPeriod(p);
    try {
      const existing = approvalByPeriod.get(p);
      if (existing) {
        await updateMutation.mutateAsync({
          approvalId: existing.id,
          input: { approvedHours: hours },
        });
      } else {
        await upsertMutation.mutateAsync({ periodYearMonth: p, approvedHours: hours });
      }
      return true;
    } catch (e) {
      const message = (e as Error).message || failure;
      setErrors((prev) => ({ ...prev, [p]: message }));
      return false;
    } finally {
      setPendingPeriod(null);
    }
  };

  const openEditor = (p: string) => {
    if (editingPeriod !== null && editingPeriod !== p) clearError(editingPeriod);
    setDraftHours(prefillFor(p));
    clearError(p);
    setEditingPeriod(p);
  };

  const handleApproveAll = async (row: MonthRow) => {
    if (row.overageHours === null) return;
    await savePeriod(row.period, row.overageHours.toFixed(2), 'Could not save approval.');
  };

  const handleAcknowledge = async (p: string) => {
    await savePeriod(p, null, 'Could not acknowledge overage.');
  };

  const handleSave = async (p: string) => {
    const trimmed = draftHours.trim();
    if (!HOURS_PATTERN.test(trimmed)) {
      setErrors((prev) => ({
        ...prev,
        [p]: 'Enter a positive decimal with up to 2 decimal places (e.g. 12.50).',
      }));
      return;
    }
    const ok = await savePeriod(p, trimmed, 'Could not save approval.');
    if (ok) setEditingPeriod(null);
  };

  const handleCancel = (p: string) => {
    setDraftHours(prefillFor(p));
    setEditingPeriod(null);
    clearError(p);
  };

  const handleAddMonth = () => {
    if (!pickerValue) return;
    if (pickerValue > period) {
      setPickerError('Pick the current or an earlier month.');
      return;
    }
    setPickerError(null);
    setAddedPeriods((prev) => (prev.includes(pickerValue) ? prev : [...prev, pickerValue]));
    openEditor(pickerValue);
    setPickerValue('');
  };

  return (
    <div id="overage-approvals">
      <Card title="Approved overage hours">
        <p className="text-[12px] text-ms-text-secondary mb-3">
          Hours approved by the PM to count over the monthly Hour Package. Any month can be
          approved after the fact; an approval must cover the month&apos;s overage hours before
          that overage can be invoiced. History is append-only.
        </p>

        <div className="divide-y divide-gray-100">
          {rows.map((row) => (
            <MonthRowView
              key={row.period}
              row={row}
              isLoading={isLoading}
              canManage={canManage}
              isEditing={editingPeriod === row.period}
              draftHours={draftHours}
              error={errors[row.period]}
              isPending={pendingPeriod === row.period}
              onDraftChange={(value) => {
                setDraftHours(value);
                clearError(row.period);
              }}
              onApproveAll={() => void handleApproveAll(row)}
              onAcknowledge={() => void handleAcknowledge(row.period)}
              onOpenEditor={() => openEditor(row.period)}
              onSave={() => void handleSave(row.period)}
              onCancel={() => handleCancel(row.period)}
            />
          ))}
        </div>

        {canManage && (
          <div className="mt-3 border-t border-gray-100 pt-3">
            <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-ms-text-muted">
              Approve another month
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <MonthYearPicker
                value={pickerValue}
                onChange={(next) => {
                  setPickerValue(next);
                  setPickerError(null);
                }}
                label="Approve another month"
                idPrefix="overage-approval-picker"
              />
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={handleAddMonth}
                disabled={!pickerValue}
              >
                Add month
              </Button>
            </div>
            {pickerError && <p className="mt-1 text-[12px] text-red-600">{pickerError}</p>}
          </div>
        )}
      </Card>
    </div>
  );
}
